import {takeEvery} from 'redux-saga';
import {call, put} from 'redux-saga/effects';
import {getQuestion, getQuestionVotes} from './Question';
import {replaceInArray, mergeInArray} from '../lib/helpers';
import {appError, busy, ready} from './Status';

const meta = {requireAuthentication: true};

const CREATE_ANSWER = 'app-qa/question/CREATE_ANSWER';
export const createAnswer = ({answer, question}) => ({type: CREATE_ANSWER, answer, question, meta});

const EDIT_ANSWER = 'app-qa/question/EDIT_ANSWER';
export const editAnswer = (answer, editing) => ({type: EDIT_ANSWER, answer, editing, meta});

const UPDATE_ANSWER = 'app-qa/question/UPDATE_ANSWER';
export const updateAnswer = (answer) => ({type: UPDATE_ANSWER, answer, meta});

const UPDATE_ANSWER_SUCCESS = 'app-qa/question/UPDATE_ANSWER_SUCCESS';
export const updateAnswerSuccess = (answer) => ({type: UPDATE_ANSWER_SUCCESS, answer});

const INCREMENT_ANSWER_COUNTER = 'app-qa/question/INCREMENT_ANSWER_COUNTER';
export const incrementAnswerCounter = (increment) => ({type: INCREMENT_ANSWER_COUNTER, increment, meta});

const INCREMENT_ANSWER_COUNTER_SUCCESS = 'app-qa/question/INCREMENT_ANSWER_COUNTER_SUCCESS';
export const incrementAnswerCounterSuccess = (increment, votes) =>
  ({type: INCREMENT_ANSWER_COUNTER_SUCCESS, increment, votes});

const ACCEPT_ANSWER = 'app-qa/question/ACCEPT_ANSWER';
export const acceptAnswer = (answer, accept) => ({type: ACCEPT_ANSWER, answer, accept, meta});

const ACCEPT_ANSWER_SUCCESS = 'app-qa/question/ACCEPT_ANSWER_SUCCESS';
export const acceptAnswerSuccess = (response) => ({type: ACCEPT_ANSWER_SUCCESS, response});

const MODERATE_ANSWER = 'app-qa/question/MODERATE_ANSWER';
export const moderateAnswer = (answer) => ({type: MODERATE_ANSWER, answer});

export const actions = {
  CREATE_ANSWER,
  EDIT_ANSWER,
  UPDATE_ANSWER,
  UPDATE_ANSWER_SUCCESS,
  INCREMENT_ANSWER_COUNTER,
  INCREMENT_ANSWER_COUNTER_SUCCESS,
  ACCEPT_ANSWER,
  ACCEPT_ANSWER_SUCCESS,
  MODERATE_ANSWER,
};

const updateAnswers = (state, updatedAnswers) => {
  const questionUpdate = {...state.currentQuestion, answers: updatedAnswers};
  return {...state, currentQuestion: questionUpdate};
};

export default function reducer(state = {}, action = {}) {
  switch (action.type) {
    case INCREMENT_ANSWER_COUNTER_SUCCESS: {
      const modifiedAnswer = {id: action.increment.answer_id, ...action.votes.result};
      const updatedAnswers = mergeInArray(state.currentQuestion.answers, modifiedAnswer);
      return updateAnswers(state, updatedAnswers);
    }
    case EDIT_ANSWER: {
      const modifiedAnswer = {id: action.answer.id, editing: action.editing};
      const updatedAnswers = mergeInArray(state.currentQuestion.answers, modifiedAnswer);
      return updateAnswers(state, updatedAnswers);
    }
    case UPDATE_ANSWER_SUCCESS: {
      const updatedAnswers = replaceInArray(state.currentQuestion.answers, action.answer);
      return updateAnswers(state, updatedAnswers);
    }
    case ACCEPT_ANSWER_SUCCESS: {
      const acceptResponse = action.response.result;
      return Object.assign({}, state, {currentQuestion: acceptResponse});
    }
    default:
      return state;
  }
}

export function sagas({qaClient}) {
  function *createAnswerSaga({answer, question}) {
    yield put(busy({blockUi: true}));
    try {
      yield call(() => qaClient.createAnswer(answer));
      yield put(getQuestion(question.url));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *updateAnswerSaga({answer}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.updateAnswer(answer));
      yield put(updateAnswerSuccess(response.result));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *incrementAnswerCounterSaga({increment}) {
    try {
      const response = yield call(() => qaClient.incrementAnswerCounter(increment));
      yield put(incrementAnswerCounterSuccess(increment, response));
      yield put(getQuestionVotes(increment.question_id));
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *acceptAnswerSaga({answer, accept}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.acceptAnswer(answer, accept));
      yield put(acceptAnswerSuccess(response));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  function *moderateAnswerSaga({answer}) {
    yield put(busy({blockUi: true}));
    try {
      const response = yield call(() => qaClient.moderateAnswer(answer));
      yield put(updateAnswerSuccess(response.result));
      yield put(ready());
    } catch (err) {
      yield put(appError({err}));
    }
  }

  return {
    *watchCreateAnswer() { yield* takeEvery(CREATE_ANSWER, createAnswerSaga); },
    *watchUpdateAnswer() { yield* takeEvery(UPDATE_ANSWER, updateAnswerSaga); },
    *watchIncrementAnswerCounter() { yield* takeEvery(INCREMENT_ANSWER_COUNTER, incrementAnswerCounterSaga); },
    *watchAcceptAnswer() { yield* takeEvery(ACCEPT_ANSWER, acceptAnswerSaga); },
    *watchModerateAnswer() { yield* takeEvery(MODERATE_ANSWER, moderateAnswerSaga); },
  };
}
